import { Label, ProgressBar, ProgressBarProps } from 'react-aria-components'

import { joinStyles } from '@/utils/app/theme'

import * as styles from './index.css'

type CircularProgressProperties = ProgressBarProps & {
    className?: string
    label?: string
}

export const CircularProgress = ({
    className,
    label,
    ...properties
}: Readonly<CircularProgressProperties>): JSX.Element => {
    const center = 16
    const strokeWidth = 3
    const radius = 16 - strokeWidth
    const circumference = 2 * Math.PI * radius

    return (
        <ProgressBar {...properties}>
            {({ percentage, valueText }) => {
                return (
                    <div
                        className={joinStyles(styles.root, className)}
                        aria-valuenow={properties.value}
                    >
                        <svg
                            width={64}
                            height={64}
                            viewBox='0 0 32 32'
                            fill='none'
                            strokeWidth={strokeWidth}
                        >
                            {/* Internal circle */}
                            <circle
                                className={styles.circle}
                                cx={center}
                                cy={center}
                                r={radius - (strokeWidth / 2 - 0.25)}
                                strokeWidth={0.5}
                            />

                            {/* External circle */}
                            <circle
                                className={styles.circle}
                                cx={center}
                                cy={center}
                                r={radius + (strokeWidth / 2 - 0.25)}
                                strokeWidth={0.5}
                            />

                            {/* Progress */}
                            <circle
                                className={styles.progress}
                                cx={center}
                                cy={center}
                                r={radius}
                                strokeDasharray={`${circumference} ${circumference}`}
                                strokeDashoffset={
                                    circumference -
                                    ((percentage ?? 60) / 100) * circumference
                                }
                                strokeLinecap='round'
                                transform='rotate(-90 16 16)'
                            />

                            {/* Text */}
                            {!!percentage && (
                                <text
                                    className={styles.percentage}
                                    x={center}
                                    y={center}
                                >
                                    {valueText}
                                </text>
                            )}
                        </svg>

                        <Label className={styles.text}>{label}</Label>
                    </div>
                )
            }}
        </ProgressBar>
    )
}
